import { Component, computed, Input, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  BlogSettings,
  BlogContent,
  BlogLayoutVariant,
  BlogPost,
  BlogPostSettings,
  SECTION_THEME,
} from './section-renderer.models';

/**
 * Full-width Blog/Posts section renderer.
 * Layout variants:
 * - grid: Three column grid of posts
 * - single-col: Single column list with large cards
 * - featured: First post featured (large), rest in grid
 */
@Component({
  selector: 'dcp-blog-renderer',
  standalone: true,
  imports: [CommonModule],
  template: `
    @if (posts().length === 0) {
      <!-- Empty state when no posts -->
      <section
        class="relative py-24 sm:py-32"
        [style.background-color]="settings().backgroundColor || theme.backgroundColor"
      >
        <div class="mx-auto max-w-7xl px-6 lg:px-8">
          <div class="text-center">
            <svg class="mx-auto h-12 w-12 opacity-30" fill="none" viewBox="0 0 24 24" stroke-width="1" [style.stroke]="theme.textMutedColor">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 7.5h1.5m-1.5 3h1.5m-7.5 3h7.5m-7.5 3h7.5m3-9h3.375c.621 0 1.125.504 1.125 1.125V18a2.25 2.25 0 01-2.25 2.25M16.5 7.5V18a2.25 2.25 0 002.25 2.25M16.5 7.5V4.875c0-.621-.504-1.125-1.125-1.125H4.125C3.504 3.75 3 4.254 3 4.875V18a2.25 2.25 0 002.25 2.25h13.5M6 7.5h3v3H6v-3z" />
            </svg>
            @if (content().title) {
              <h2 class="mt-4 text-xl font-semibold" [style.color]="theme.textColor">
                {{ content().title }}
              </h2>
            }
            <p class="mt-2 text-sm" [style.color]="theme.textMutedColor">
              No posts to display yet.
            </p>
          </div>
        </div>
      </section>
    } @else {
      @switch (layoutVariant()) {
        <!-- Single Column Layout -->
        @case ('single-col') {
          <section
            class="relative py-24 sm:py-32"
            [style.background-color]="settings().backgroundColor || theme.backgroundColor"
          >
            <div class="mx-auto max-w-7xl px-6 lg:px-8">
              <div class="mx-auto max-w-2xl text-center">
                @if (content().title) {
                  <h2 class="text-3xl font-bold tracking-tight sm:text-4xl" [style.color]="theme.textColor">
                    {{ content().title }}
                  </h2>
                }
                @if (content().subtitle) {
                  <p class="mt-2 text-lg leading-8" [style.color]="theme.textMutedColor">
                    {{ content().subtitle }}
                  </p>
                }
              </div>
              <div class="mx-auto mt-16 max-w-2xl">
                <div class="space-y-20">
                  @for (post of posts(); track $index) {
                    <article class="flex flex-col items-start">
                      @if (post.imageUrl) {
                        <div class="relative w-full">
                          <img
                            [src]="post.imageUrl"
                            [alt]="post.title"
                            class="aspect-[16/9] w-full rounded-2xl object-cover sm:aspect-[2/1] lg:aspect-[3/2]"
                          />
                          <div class="absolute inset-0 rounded-2xl ring-1 ring-inset ring-gray-900/10"></div>
                        </div>
                      }
                      <div class="max-w-xl" [class.mt-8]="post.imageUrl">
                        <div class="flex items-center gap-x-4 text-xs">
                          @if (settings().showDate !== false && post.date) {
                            <time [style.color]="theme.textMutedColor">{{ post.date }}</time>
                          }
                          @if (settings().showCategory !== false && post.category) {
                            <span class="relative z-10 rounded-full px-3 py-1.5 font-medium"
                              [style.background-color]="theme.primaryColor + '20'" [style.color]="theme.primaryColor">
                              {{ post.category }}
                            </span>
                          }
                        </div>
                        <div class="group relative">
                          <h3 class="mt-3 text-lg font-semibold leading-6" [style.color]="theme.textColor">
                            @if (post.url) {
                              <a [href]="post.url"><span class="absolute inset-0"></span>{{ post.title }}</a>
                            } @else {
                              {{ post.title }}
                            }
                          </h3>
                          @if (settings().showExcerpt !== false && post.excerpt) {
                            <p class="mt-5 line-clamp-3 text-sm leading-6" [style.color]="theme.textMutedColor">{{ post.excerpt }}</p>
                          }
                        </div>
                      </div>
                    </article>
                  }
                </div>
              </div>
            </div>
          </section>
        }

        <!-- Featured Layout: First post large, rest in 2-column grid -->
        @case ('featured') {
          <section
            class="relative py-24 sm:py-32"
            [style.background-color]="settings().backgroundColor || theme.backgroundColor"
          >
            <div class="mx-auto max-w-7xl px-6 lg:px-8">
              <div class="mx-auto max-w-2xl text-center">
                @if (content().title) {
                  <h2 class="text-3xl font-bold tracking-tight sm:text-4xl" [style.color]="theme.textColor">
                    {{ content().title }}
                  </h2>
                }
                @if (content().subtitle) {
                  <p class="mt-2 text-lg leading-8" [style.color]="theme.textMutedColor">
                    {{ content().subtitle }}
                  </p>
                }
              </div>
              
              <!-- Featured (first) post -->
              @if (posts()[0]; as featuredPost) {
                <article class="mt-16 lg:flex lg:gap-x-10">
                  @if (featuredPost.imageUrl) {
                    <div class="relative lg:w-1/2">
                      <img [src]="featuredPost.imageUrl" [alt]="featuredPost.title"
                        class="aspect-[16/9] w-full rounded-2xl object-cover lg:aspect-[3/2]" />
                      <div class="absolute inset-0 rounded-2xl ring-1 ring-inset ring-gray-900/10"></div>
                    </div>
                  }
                  <div class="mt-8 lg:mt-0 lg:w-1/2 lg:flex lg:flex-col lg:justify-center" [class.lg:w-full]="!featuredPost.imageUrl">
                    <div class="flex items-center gap-x-4 text-xs">
                      @if (settings().showDate !== false && featuredPost.date) {
                        <time [style.color]="theme.textMutedColor">{{ featuredPost.date }}</time>
                      }
                      @if (settings().showCategory !== false && featuredPost.category) {
                        <span class="rounded-full px-3 py-1.5 font-medium"
                          [style.background-color]="theme.primaryColor + '20'" [style.color]="theme.primaryColor">
                          {{ featuredPost.category }}
                        </span>
                      }
                    </div>
                    <h3 class="mt-3 text-2xl font-bold leading-8 lg:text-3xl" [style.color]="theme.textColor">
                      @if (featuredPost.url) {
                        <a [href]="featuredPost.url" class="hover:underline">{{ featuredPost.title }}</a>
                      } @else {
                        {{ featuredPost.title }}
                      }
                    </h3>
                    @if (settings().showExcerpt !== false && featuredPost.excerpt) {
                      <p class="mt-4 text-base leading-7" [style.color]="theme.textMutedColor">{{ featuredPost.excerpt }}</p>
                    }
                  </div>
                </article>
              }

              <!-- Rest of posts in 2-column grid -->
              @if (posts().length > 1) {
                <div class="mt-16 grid max-w-2xl grid-cols-1 gap-x-8 gap-y-12 lg:mx-0 lg:max-w-none lg:grid-cols-2">
                  @for (post of posts().slice(1); track $index) {
                    <article class="flex flex-col items-start">
                      @if (post.imageUrl) {
                        <div class="relative w-full">
                          <img [src]="post.imageUrl" [alt]="post.title"
                            class="aspect-[16/9] w-full rounded-xl object-cover" />
                          <div class="absolute inset-0 rounded-xl ring-1 ring-inset ring-gray-900/10"></div>
                        </div>
                      } @else {
                        <div class="relative w-full aspect-[16/9] rounded-xl flex items-center justify-center"
                          [style.background-color]="theme.surfaceColor">
                          <svg class="h-10 w-10 opacity-20" fill="none" viewBox="0 0 24 24" stroke-width="1" [style.stroke]="theme.textMutedColor">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M12 7.5h1.5m-1.5 3h1.5m-7.5 3h7.5m-7.5 3h7.5m3-9h3.375c.621 0 1.125.504 1.125 1.125V18a2.25 2.25 0 01-2.25 2.25M16.5 7.5V18a2.25 2.25 0 002.25 2.25M16.5 7.5V4.875c0-.621-.504-1.125-1.125-1.125H4.125C3.504 3.75 3 4.254 3 4.875V18a2.25 2.25 0 002.25 2.25h13.5M6 7.5h3v3H6v-3z" />
                          </svg>
                        </div>
                      }
                      <div class="max-w-xl mt-6">
                        <div class="flex items-center gap-x-4 text-xs">
                          @if (settings().showDate !== false && post.date) {
                            <time [style.color]="theme.textMutedColor">{{ post.date }}</time>
                          }
                          @if (settings().showCategory !== false && post.category) {
                            <span class="rounded-full px-3 py-1.5 font-medium"
                              [style.background-color]="theme.primaryColor + '20'" [style.color]="theme.primaryColor">
                              {{ post.category }}
                            </span>
                          }
                        </div>
                        <h3 class="mt-3 text-lg font-semibold leading-6" [style.color]="theme.textColor">
                          @if (post.url) {
                            <a [href]="post.url" class="hover:underline">{{ post.title }}</a>
                          } @else {
                            {{ post.title }}
                          }
                        </h3>
                        @if (settings().showExcerpt !== false && post.excerpt) {
                          <p class="mt-3 line-clamp-2 text-sm leading-6" [style.color]="theme.textMutedColor">{{ post.excerpt }}</p>
                        }
                      </div>
                    </article>
                  }
                </div>
              }
            </div>
          </section>
        }

        <!-- Grid Layout (default): Three column grid -->
        @default {
          <section
            class="relative py-24 sm:py-32"
            [style.background-color]="settings().backgroundColor || theme.backgroundColor"
          >
            <div class="mx-auto max-w-7xl px-6 lg:px-8">
              <div class="mx-auto max-w-2xl text-center">
                @if (content().title) {
                  <h2 class="text-3xl font-bold tracking-tight sm:text-4xl" [style.color]="theme.textColor">
                    {{ content().title }}
                  </h2>
                }
                @if (content().subtitle) {
                  <p class="mt-2 text-lg leading-8" [style.color]="theme.textMutedColor">
                    {{ content().subtitle }}
                  </p>
                }
              </div>
              <div class="mx-auto mt-16 grid max-w-2xl grid-cols-1 gap-x-8 gap-y-20 lg:mx-0 lg:max-w-none lg:grid-cols-3">
                @for (post of posts(); track $index) {
                  <article class="flex flex-col items-start">
                    @if (post.imageUrl) {
                      <div class="relative w-full">
                        <img [src]="post.imageUrl" [alt]="post.title"
                          class="aspect-[16/9] w-full rounded-2xl object-cover sm:aspect-[2/1] lg:aspect-[3/2]" />
                        <div class="absolute inset-0 rounded-2xl ring-1 ring-inset ring-gray-900/10"></div>
                      </div>
                    } @else {
                      <div class="relative w-full aspect-[16/9] rounded-2xl flex items-center justify-center sm:aspect-[2/1] lg:aspect-[3/2]"
                        [style.background-color]="theme.surfaceColor">
                        <svg class="h-12 w-12 opacity-20" fill="none" viewBox="0 0 24 24" stroke-width="1" [style.stroke]="theme.textMutedColor">
                          <path stroke-linecap="round" stroke-linejoin="round" d="M12 7.5h1.5m-1.5 3h1.5m-7.5 3h7.5m-7.5 3h7.5m3-9h3.375c.621 0 1.125.504 1.125 1.125V18a2.25 2.25 0 01-2.25 2.25M16.5 7.5V18a2.25 2.25 0 002.25 2.25M16.5 7.5V4.875c0-.621-.504-1.125-1.125-1.125H4.125C3.504 3.75 3 4.254 3 4.875V18a2.25 2.25 0 002.25 2.25h13.5M6 7.5h3v3H6v-3z" />
                        </svg>
                      </div>
                    }
                    <div class="max-w-xl mt-8">
                      <div class="flex items-center gap-x-4 text-xs">
                        @if (settings().showDate !== false && post.date) {
                          <time [style.color]="theme.textMutedColor">{{ post.date }}</time>
                        }
                        @if (settings().showCategory !== false && post.category) {
                          <span class="relative z-10 rounded-full px-3 py-1.5 font-medium"
                            [style.background-color]="theme.primaryColor + '20'" [style.color]="theme.primaryColor">
                            {{ post.category }}
                          </span>
                        }
                      </div>
                      <div class="group relative">
                        <h3 class="mt-3 text-lg font-semibold leading-6" [style.color]="theme.textColor">
                          @if (post.url) {
                            <a [href]="post.url" class="hover:underline"><span class="absolute inset-0"></span>{{ post.title }}</a>
                          } @else {
                            {{ post.title }}
                          }
                        </h3>
                        @if (settings().showExcerpt !== false && post.excerpt) {
                          <p class="mt-5 line-clamp-3 text-sm leading-6" [style.color]="theme.textMutedColor">{{ post.excerpt }}</p>
                        }
                      </div>
                    </div>
                  </article>
                }
              </div>
            </div>
          </section>
        }
      }
    }
  `,
})
export class BlogRendererComponent {
  /** Theme injected from parent fullwidth-preview */
  private readonly themeSignal = inject(SECTION_THEME);
  protected get theme() { return this.themeSignal(); }

  private readonly _settings = signal<BlogSettings>({});
  private readonly _content = signal<BlogContent>({});

  @Input() set settingsInput(value: Record<string, unknown>) {
    this._settings.set(value as BlogSettings);
  }

  @Input() set contentInput(value: Record<string, unknown>) {
    const content = value as unknown as BlogContent;
    this._content.set(content);
  }

  readonly settings = this._settings.asReadonly();
  readonly content = this._content.asReadonly();

  readonly layoutVariant = computed<BlogLayoutVariant>(() => {
    const settings = this._settings();
    // Check both 'layoutVariant' (standard) and 'variant' (from form) 
    const variant = (settings.layoutVariant || (settings as Record<string, unknown>)['variant']) as string | undefined;
    // Handle legacy 'three-cols' value
    if (variant === 'three-cols') return 'grid';
    return (variant as BlogLayoutVariant) || 'grid';
  });

  /** Computed posts array that merges settings structure with content texts */
  readonly posts = computed<BlogPost[]>(() => {
    const settings = this._settings();
    const content = this._content();
    
    // New format: settings.posts + content.postTexts
    if (settings.posts && settings.posts.length > 0) {
      const postTexts = content.postTexts || {};
      
      return settings.posts.map(postSettings => {
        const texts = postTexts[postSettings.id] || { title: '', excerpt: '' };
        // imageUrl may be pre-resolved by page-editor, or we use it directly
        const imageUrl = (postSettings as BlogPostSettings & { imageUrl?: string }).imageUrl;
        return {
          title: texts.title || '',
          excerpt: texts.excerpt || '',
          date: postSettings.date,
          url: postSettings.url,
          imageUrl,
        } as BlogPost;
      });
    }
    
    // Legacy format: content.posts array
    return content.posts || [];
  });
}
